import { MouseEvent, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Avatar, Box, IconButton } from '@mui/material';
import { FileInputClasses, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { ImageInput } from './ImageInput';

type AvatarImageValue = {
  src?: string;
  title?: string;
  rawFile?: File;
};

export type AvatarImageInputProps = {
  source: string;
};

const AVATAR_SIZE = 128;

export const AvatarImageInput = (props: AvatarImageInputProps) => {

  const value = useWatch({ name: props.source }) as AvatarImageValue | null | undefined;
  const form = useFormContext();
  const translate = useTranslate();

  useEffect(() => {

    if (!value?.rawFile || !value.src) {

      return undefined;

    }

    return () => {

      if (value?.src) URL.revokeObjectURL(value.src);

    };

  }, [value?.rawFile, value?.src]);

  const removeImage = (event: MouseEvent<HTMLButtonElement>) => {

    event.preventDefault();
    event.stopPropagation();

    form.setValue(props.source, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

  };

  return (
    <ImageInput
      source={props.source}
      label={false}
      accept={{ 'image/*': [] }}
      multiple={false}
      sx={{
        width: 'fit-content',
        marginTop: '10px',
        [`& .${FileInputClasses.dropZone}`]: {
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 'none',
          display: 'flex',
          height: AVATAR_SIZE,
          justifyContent: 'center',
          overflow: 'visible',
          padding: 0,
          width: AVATAR_SIZE,
        },
      }}
      placeholder={(
        <Box
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            position: 'relative',
            '&:hover .AvatarImageInput-removeButton, & .AvatarImageInput-removeButton:focus-visible': {
              opacity: 1,
              pointerEvents: 'auto',
            },
          }}
        >
          <Avatar
            variant="rounded"
            src={value?.src}
            alt={value?.title || ''}
            sx={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.secondary',
              pointerEvents: 'none',
            }}
          >
            {!value?.src && (
              <AddIcon sx={{ fontSize: 36 }}/>
            )}
          </Avatar>
          {value?.src && (
            <IconButton
              type="button"
              className="AvatarImageInput-removeButton"
              aria-label={translate('ra.input.image.remove_current')}
              onClick={removeImage}
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                width: 40,
                height: 40,
                bgcolor: 'background.paper',
                boxShadow: 2,
                color: 'error.main',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 150ms ease-in-out',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'background.paper',
                  color: 'error.dark',
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 26 }}/>
            </IconButton>
          )}
        </Box>
      )}
    />
  );

};
